import type { AppearanceSettings } from '@/types/monitoring'

export const appearanceDefaults: AppearanceSettings = {
  theme: 'light',
  locale: 'en',
  faviconUrl: '',
  backgroundUrl: '',
  backgroundMode: 'solid',
  backgroundColor: '#ffffff',
  gradientStart: '#e8f4ee',
  gradientEnd: '#e5ecff',
  gradientAngle: 135,
  gradientAnimated: false,
  animationSeconds: 24,
  imageFilter: 'none',
  imageBrightness: 100,
  imageBlur: 0,
  imageOverlayColor: '#ffffff',
  imageOverlayOpacity: 20,
  imageMotion: 'none',
  cardGlass: false,
  cardOpacity: 82,
  cardGradientStart: '#ffffff',
  cardGradientEnd: '#f0f6f4',
  cardGradientAngle: 135,
  cardBlur: 18,
}

export function normalizeAppearance(value: unknown): AppearanceSettings {
  const raw =
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {}
  const result = { ...appearanceDefaults }
  for (const key of Object.keys(result) as (keyof AppearanceSettings)[]) {
    const snake = key.replace(/[A-Z]/g, (letter) => '_' + letter.toLowerCase())
    const candidate = raw[key] ?? raw[snake]
    if (typeof candidate === typeof result[key]) Object.assign(result, { [key]: candidate })
  }
  const oneOf = <T extends string>(candidate: T, values: readonly T[], fallback: T): T =>
    values.includes(candidate) ? candidate : fallback
  result.theme = oneOf(result.theme, ['light', 'dark', 'system'], 'light')
  result.locale = oneOf(result.locale, ['en', 'zh-CN'], 'en')
  result.backgroundMode = oneOf(result.backgroundMode, ['solid', 'gradient', 'image'], 'solid')
  if (raw.backgroundMode == null && raw.background_mode == null && result.backgroundUrl)
    result.backgroundMode = 'image'
  result.imageFilter = oneOf(
    result.imageFilter,
    ['none', 'soft', 'monochrome', 'warm', 'cool'],
    'none',
  )
  result.imageMotion = oneOf(result.imageMotion, ['none', 'drift', 'breathe'], 'none')
  const colors = [
    'backgroundColor',
    'gradientStart',
    'gradientEnd',
    'imageOverlayColor',
    'cardGradientStart',
    'cardGradientEnd',
  ] as const
  for (const key of colors)
    result[key] = /^#[\da-f]{6}$/i.test(result[key])
      ? result[key].toLowerCase()
      : appearanceDefaults[key]
  const ranges = {
    gradientAngle: [0, 360],
    animationSeconds: [8, 120],
    imageBrightness: [25, 150],
    imageBlur: [0, 20],
    imageOverlayOpacity: [0, 80],
    cardOpacity: [20, 100],
    cardGradientAngle: [0, 360],
    cardBlur: [0, 40],
  } as const
  for (const key of Object.keys(ranges) as (keyof typeof ranges)[]) {
    const [min, max] = ranges[key]
    result[key] = Number.isFinite(result[key])
      ? Math.round(Math.max(min, Math.min(max, result[key])))
      : appearanceDefaults[key]
  }
  const safeUrl = (url: string) =>
    ![...url].some(
      (character) =>
        character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127 || character === '\\',
    ) &&
    url.length <= 2048 &&
    (!url || (url.startsWith('/') && !url.startsWith('//')) || /^https:\/\/[^/\s]+/i.test(url))
  if (!safeUrl(result.backgroundUrl)) result.backgroundUrl = ''
  if (!safeUrl(result.faviconUrl)) result.faviconUrl = ''
  return result
}

export function rgba(hex: string, opacity: number): string {
  const channels = [1, 3, 5].map((start) => parseInt(hex.slice(start, start + 2), 16))
  return `rgba(${channels.join(',')},${opacity / 100})`
}

export function panelColors(
  settings: AppearanceSettings,
  dark: boolean,
): { start: string; end: string; ink: string; muted: string } {
  const start =
    dark && settings.cardGradientStart === appearanceDefaults.cardGradientStart
      ? '#111827'
      : settings.cardGradientStart
  const end =
    dark && settings.cardGradientEnd === appearanceDefaults.cardGradientEnd
      ? '#1e293b'
      : settings.cardGradientEnd
  const luminance = (hex: string) =>
    [1, 3, 5]
      .map((index) => parseInt(hex.slice(index, index + 2), 16) / 255)
      .map((channel) => (channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4))
      .reduce((sum, channel, index) => sum + channel * ([0.2126, 0.7152, 0.0722][index] ?? 0), 0)
  const lightInk = (luminance(start) + luminance(end)) / 2 < 0.2
  return {
    start,
    end,
    ink: lightInk ? '#f8fafc' : '#17242d',
    muted: lightInk ? '#cbd5e1' : '#526374',
  }
}

export function imageFilter(settings: AppearanceSettings): string {
  const filters = {
    none: '',
    soft: 'saturate(.75) contrast(.9)',
    monochrome: 'grayscale(1)',
    warm: 'sepia(.3) saturate(1.15)',
    cool: 'sepia(.2) hue-rotate(155deg) saturate(.85)',
  }
  return `${filters[settings.imageFilter]} brightness(${settings.imageBrightness / 100}) blur(${settings.imageBlur}px)`
}
